import * as React from "react";
import { Form, Formik, FormikProps } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Stack, Button, Grid } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { DatePickerField, DATE_PICKER_FORMAT } from "components/formikFields/DatePickerField/DatePickerField";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { ApplicationEventType, Branch, ClientsService } from "gen/clients";
import { supportedEventTypeOptions } from "utils/applicationEventUtils";
import { SelectOption } from "@mui/base";
import { useQuery } from "react-query";

export interface FilterValues {
  from: Date;
  to: Date;
  eventType: SelectOption<ApplicationEventType>;
  branchId: string;
  clientId: string;
  username: string;
}

interface Props {
  from: Date;
  to: Date;
  eventType: SelectOption<ApplicationEventType>;
  branches: Branch[];
  onChange: (values: FilterValues) => void;
  onRefresh: () => void;
}

enum FieldNames {
  from = "from",
  to = "to",
  eventType = "eventType",
  branchId = "branchId",
  client = "client",
  username = "username"
}

interface Values {
  [FieldNames.from]: string;
  [FieldNames.to]: string;
  [FieldNames.eventType]: SelectOption<ApplicationEventType>;
  [FieldNames.branchId]: string;
  [FieldNames.client]: SelectOption<string> | null;
  [FieldNames.username]: string;
}

dayjs.extend(utc);
dayjs.extend(timezone);

const ALL_BRANCHES_VALUE = "";

const Filter: React.FC<Props> = ({ from, to, eventType, branches, onChange, onRefresh }) => {
  const formikRef = React.useRef<FormikProps<Values>>(null);
  const lastSubmittedValuesRef = React.useRef<Values | null>(null);
  const [clientSearchText, setClientSearchText] = React.useState<string>();

  const { data: matchingClients, isLoading: isLoadingClients } = useQuery(["listClients", clientSearchText], {
    queryFn: () => ClientsService.listClients({ companyName: clientSearchText, limit: 30 })
  });

  const clientOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!matchingClients?.items.length) {
      return [];
    }
    return matchingClients.items.map(client => ({
      label: client.companyName,
      value: client.id
    }));
  }, [matchingClients?.items]);

  const branchOptions = React.useMemo(
    () => [
      { value: ALL_BRANCHES_VALUE, label: "All Branches" },
      ...branches.map(branch => ({
        value: branch.id,
        label: branch.name
      }))
    ],
    [branches]
  );

  const initialValue = React.useMemo(
    () => ({
      from: dayjs(from).format(DATE_PICKER_FORMAT),
      to: dayjs(to).format(DATE_PICKER_FORMAT),
      eventType,
      branchId: ALL_BRANCHES_VALUE,
      client: null as SelectOption<string> | null,
      username: ""
    }),
    [from, to, eventType]
  );

  const validate = React.useCallback((values: Values) => {
    const from = values[FieldNames.from];
    const to = values[FieldNames.to];
    const eventType = values[FieldNames.eventType];
    const fromValue = dayjs(from, DATE_PICKER_FORMAT);
    const toValue = dayjs(to, DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};

    if (!from) {
      errors[FieldNames.from] = "Required";
    }
    if (!to) {
      errors[FieldNames.to] = "Required";
    }
    if (!eventType) {
      errors[FieldNames.eventType] = "Required";
    }
    if (from && to && toValue.isBefore(fromValue)) {
      errors[FieldNames.to] = "Start date must be before end date";
    }

    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: Values) => {
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toDate();
      const toValue = dayjs.tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles").endOf("day").toDate();
      lastSubmittedValuesRef.current = values;
      onChange({
        from: fromValue,
        to: toValue,
        eventType: values[FieldNames.eventType],
        branchId: values[FieldNames.branchId],
        clientId: values[FieldNames.client]?.value || "",
        username: values[FieldNames.username]?.trim() || ""
      });
    },
    [onChange]
  );

  const onFormFieldObserverChange = React.useCallback(() => {
    const formValues = formikRef?.current?.values;
    if (formValues) {
      const lastValues = lastSubmittedValuesRef.current;
      if (
        lastValues &&
        lastValues[FieldNames.from] === formValues[FieldNames.from] &&
        lastValues[FieldNames.to] === formValues[FieldNames.to] &&
        lastValues[FieldNames.eventType] === formValues[FieldNames.eventType] &&
        lastValues[FieldNames.branchId] === formValues[FieldNames.branchId] &&
        lastValues[FieldNames.client]?.value === formValues[FieldNames.client]?.value
      ) {
        return;
      }

      const errors = validate(formValues);
      if (Object.keys(errors).length === 0) {
        onSubmit(formValues);
      }
    }
  }, [onSubmit, validate]);

  return (
    <Formik
      initialValues={initialValue}
      validate={validate}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formikRef}
    >
      <Form autoComplete="off" autoCorrect="off">
        <FormFieldObserver fieldName={FieldNames.from} onChange={onFormFieldObserverChange} />
        <FormFieldObserver fieldName={FieldNames.to} onChange={onFormFieldObserverChange} />
        <FormFieldObserver fieldName={FieldNames.eventType} onChange={onFormFieldObserverChange} />
        <FormFieldObserver fieldName={FieldNames.branchId} onChange={onFormFieldObserverChange} />
        <FormFieldObserver fieldName={FieldNames.client} onChange={onFormFieldObserverChange} />

        <Grid container={true} spacing={2} alignItems="flex-start">
          <Grid item={true} xs={12} sm={4}>
            <SelectOneAutocompleteField
              name={FieldNames.eventType}
              label="Event Type"
              options={supportedEventTypeOptions}
              required={true}
              variant="standard"
              matchFrom="any"
            />
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <DatePickerField name={FieldNames.from} label="Start date" required={true} disableFuture={true} />
              <DatePickerField name={FieldNames.to} label="End date" required={true} disableFuture={true} />
            </Stack>
          </Grid>
          <Grid item={true} xs={12} sm={2} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Grid>
        </Grid>
        <Grid container={true} spacing={2} alignItems="flex-start" sx={{ mt: 0 }}>
          <Grid item={true} xs={12} sm={4}>
            <SelectOneField name={FieldNames.branchId} label="Branch" options={branchOptions} variant="standard" />
          </Grid>
          <Grid item={true} xs={12} sm={4}>
            <SelectOneAutocompleteField
              name={FieldNames.client}
              options={clientOptions}
              onInputTextChange={setClientSearchText}
              areOptionsLoading={isLoadingClients}
              suppressFilterOptions={true}
              placeholder="Type company name"
              label="Client"
              variant="standard"
            />
          </Grid>
          <Grid item={true} xs={12} sm={4}>
            <TextInputField
              name={FieldNames.username}
              label="User"
              placeholder="Enter username"
              variant="standard"
              onBlur={() => {
                const formValues = formikRef?.current?.values;
                if (formValues) {
                  const errors = validate(formValues);
                  if (Object.keys(errors).length === 0) {
                    onSubmit(formValues);
                  }
                }
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const formValues = formikRef?.current?.values;
                  if (formValues) {
                    const errors = validate(formValues);
                    if (Object.keys(errors).length === 0) {
                      onSubmit(formValues);
                    }
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export { Filter };
